import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
/**
 * Beta feedback and feature flags in the Admin (docs/specs/beta.md, B-69, PRD §1.6): merchants send a bug, an idea or a
 * question from any page; the platform triages weekly in the Super Admin and answers on the item.
 */
export const FEEDBACK_KINDS = ["bug", "idea", "question"];
export const FEEDBACK_STATUSES = ["new", "triaged", "planned", "fixed", "wont_fix"];
export const feedbackSchema = z.object({
    id: z.uuid(),
    kind: z.enum(FEEDBACK_KINDS),
    message: z.string(),
    /** The Admin page it was sent from (`/orders/…`). */
    page: z.string(),
    status: z.enum(FEEDBACK_STATUSES),
    /** Sobthik's answer, shown to the merchant. */
    response: z.string().nullable(),
    createdAt: z.iso.datetime(),
    triagedAt: z.iso.datetime().nullable(),
});
export const feedbackListSchema = z.object({ data: z.array(feedbackSchema) });
export const submitFeedbackSchema = z
    .object({
    kind: z.enum(FEEDBACK_KINDS),
    message: z.string().trim().min(5).max(2000),
    page: z.string().trim().max(300).default(""),
    appVersion: z.string().trim().max(40).optional(),
})
    .strict();
export const submitFeedback = defineEndpoint({
    tag: "feedback",
    name: "submitFeedback",
    method: "POST",
    path: "/v1/feedback",
    access: { signedIn: true },
    summary: "Send a bug report, an idea or a question to Sobthik",
    body: submitFeedbackSchema,
    status: 201,
    response: feedbackSchema,
});
export const listMyFeedback = defineEndpoint({
    tag: "feedback",
    name: "listMyFeedback",
    method: "GET",
    path: "/v1/feedback",
    access: { signedIn: true },
    summary: "What the caller sent, newest first, with Sobthik's answers",
    status: 200,
    response: feedbackListSchema,
});
// Feature flags (B-63 mechanism; the Admin hides unfinished fixes behind them during the beta) -----------------------
export const enabledFlagsSchema = z.object({ enabled: z.array(z.string()) });
export const listEnabledFlags = defineEndpoint({
    tag: "feedback",
    name: "listEnabledFlags",
    method: "GET",
    path: "/v1/feature-flags",
    access: { signedIn: true },
    summary: "Feature flags that are on for this business",
    status: 200,
    response: enabledFlagsSchema,
});
// Super Admin: weekly triage ------------------------------------------------------------------------------------------
export const platformFeedbackSchema = feedbackSchema.extend({
    tenantId: z.uuid(),
    businessName: z.string(),
    internalNote: z.string().nullable(),
    appVersion: z.string().nullable(),
});
export const platformFeedbackListSchema = z.object({
    data: z.array(platformFeedbackSchema),
    counts: z.record(z.enum(FEEDBACK_STATUSES), z.number().int().nonnegative()),
});
export const platformFeedbackQuerySchema = z.object({
    status: z.enum(FEEDBACK_STATUSES).optional(),
    kind: z.enum(FEEDBACK_KINDS).optional(),
});
export const listPlatformFeedback = defineEndpoint({
    tag: "platform-admin",
    name: "listPlatformFeedback",
    method: "GET",
    path: "/v1/platform/feedback",
    access: { platformAdmin: true },
    summary: "Feedback of every business, newest first (last 200), with counts by status",
    query: platformFeedbackQuerySchema,
    status: 200,
    response: platformFeedbackListSchema,
});
export const triageFeedbackSchema = z
    .object({
    tenantId: z.uuid(),
    status: z.enum(FEEDBACK_STATUSES),
    /** Shown to the merchant; empty keeps none. */
    response: z.string().trim().max(2000).default(""),
    /** Only for Sobthik (also the audit reason). */
    internalNote: z.string().trim().min(1).max(2000),
})
    .strict();
export const triageFeedback = defineEndpoint({
    tag: "platform-admin",
    name: "triageFeedback",
    method: "POST",
    path: "/v1/platform/feedback/{id}/triage",
    access: { platformAdmin: true },
    summary: "Set a feedback item's status and answer (audited)",
    params: idParamsSchema,
    body: triageFeedbackSchema,
    status: 200,
    response: platformFeedbackSchema,
});
//# sourceMappingURL=feedback.js.map